import { useEffect, useRef, useState, type Dispatch, type RefObject, type SetStateAction } from 'react'

export interface MenuItem {
	label: string
	hint?: string
	danger?: boolean
	onSelect: () => void
}

/** Dismisses an open popup when the pointer goes down anywhere outside `ref`. */
function useCloseOnOutsideClick(
	ref: RefObject<HTMLElement | null>,
	open: boolean,
	setOpen: Dispatch<SetStateAction<boolean>>
) {
	useEffect(() => {
		if (!open) {
			return
		}
		function onDown(e: MouseEvent) {
			if (ref.current && !ref.current.contains(e.target as Node)) {
				setOpen(false)
			}
		}
		document.addEventListener('mousedown', onDown)
		return () => document.removeEventListener('mousedown', onDown)
	}, [open, ref, setOpen])
}

/**
 * The "⋯" menu. Deliberately holds the rarely-used actions (preview link,
 * copy-all, remove) so a row itself stays a status plus one obvious action.
 *
 * The dropdown escapes its card — whatever list this sits in must NOT clip
 * with `overflow-hidden`, or the menu on the last rows gets cut off.
 */
export function OverflowMenu({ items, label }: { items: MenuItem[]; label: string }) {
	const [open, setOpen] = useState(false)
	const ref = useRef<HTMLDivElement>(null)

	useCloseOnOutsideClick(ref, open, setOpen)

	useEffect(() => {
		if (!open) {
			return
		}
		function onKey(e: KeyboardEvent) {
			if (e.key === 'Escape') {
				setOpen(false)
			}
		}
		document.addEventListener('keydown', onKey)
		return () => document.removeEventListener('keydown', onKey)
	}, [open])

	return (
		<div
			ref={ref}
			className="relative shrink-0"
		>
			<button
				type="button"
				aria-label={label}
				aria-expanded={open}
				onClick={() => setOpen((v) => !v)}
				className={
					'h-8 w-8 rounded-nayda-sm border text-[15px] leading-none text-ink-soft transition-colors ' +
					(open ? 'border-line-strong bg-surface' : 'border-transparent hover:bg-surface')
				}
			>
				⋯
			</button>
			{open && (
				<div className="absolute right-0 z-30 mt-1 w-[248px] overflow-hidden rounded-nayda border border-line bg-paper shadow-lg">
					{items.map((item) => (
						<button
							key={item.label}
							type="button"
							onClick={() => {
								item.onSelect()
								setOpen(false)
							}}
							className={
								'block w-full px-3 py-2.5 text-left transition-colors hover:bg-surface ' +
								'border-b border-line last:border-b-0 ' +
								(item.danger ? 'text-[#B23B2A]' : 'text-ink')
							}
						>
							<span className="block text-[13px] font-medium">{item.label}</span>
							{item.hint && <span className="mt-0.5 block text-[11px] text-ink-mute">{item.hint}</span>}
						</button>
					))}
				</div>
			)}
		</div>
	)
}
